/**
 * Firestore data layer for Chore Champion.
 *
 * This module replaces the Parse data layer. It keeps the small record API the
 * Vue components already use (`get`, `set`, `save`, `destroy`) so the UI code
 * stays readable, but every read and write goes to Firestore.
 *
 * Firestore collections:
 *   users/{uid}            profile: email, pin, theme, logins
 *   parents/{uid}          one per account, holds stripe and patreon fields
 *   kids/{kidId}           child profile, parentId points at parents/{uid}
 *   chores/{choreId}       childId points at kids/{kidId}
 *   rewards/{rewardId}     seeded reward catalog
 *   customRewards/{id}     rewards a parent creates
 *   linkableDevices/{code} child device pairing, the code is the secret
 *   orders/{id}            reward purchases
 *   config/app             shared config such as the default tax rate
 */
import { firestore, auth, FieldValue } from "./firebase";
import * as ModernModals from "modern-modals";
import CCStripe from "./stripe";

/**
 * A Firestore backed record. Mirrors the shape of the Parse objects the app
 * used to hold in memory, so components can keep calling get/set/save.
 */
export class Record {
  constructor(collection, id, data = {}) {
    this.collection = collection;
    this.id = id;
    this.data = Object.assign({}, data);
    // Fields the caller changed. A save only writes those, so a stale copy of
    // a record cannot overwrite a value another device just changed.
    this.changed = {};
    this.pending = null;
    this.isNew = true;
  }

  get(key) {
    if (this.collection === "parents" && key === "user") {
      return getUserProfileSync();
    }
    if (this.collection === "kids" && key === "customReward") {
      const rewardId = this.data.customRewardId;
      if (!rewardId) return null;
      return new Record("customRewards", rewardId, {
        name: this.data.customRewardName || ""
      });
    }
    return this.data[key];
  }

  set(key, value) {
    if (typeof key === "object" && value === undefined) {
      Object.keys(key).forEach((field) => this.set(field, key[field]));
      return this;
    }
    if (this.collection === "parents" && key === "user") {
      this.data.userId = value ? value.id || value : null;
      this.changed.userId = true;
      return this;
    }
    if (this.collection === "kids" && key === "customReward") {
      this.data.customRewardId = value ? value.id || value : null;
      this.data.customRewardName =
        value && value.data ? value.data.name || null : null;
      this.changed.customRewardId = true;
      this.changed.customRewardName = true;
      return this;
    }
    this.data[key] = value;
    this.changed[key] = true;
    // A plain value replaces an increment on the same field. Otherwise the
    // increment is applied on top of the value that was just set, and the
    // two do not agree on what the field should hold.
    if (this.pending && key in this.pending) {
      delete this.pending[key];
    }
    return this;
  }

  /**
   * Adds to a number field on the server instead of overwriting it.
   *
   * The points counter is read on one device and written on another, so a
   * plain read then write loses an update when two devices finish a chore at
   * the same time. The increment is applied by Firestore, and the local copy
   * is refreshed after the write.
   */
  increment(key, amount) {
    if (!this.pending) this.pending = {};
    this.pending[key] = (this.pending[key] || 0) + amount;
    // The local copy moves with the increment, so it does not lag behind the
    // server and get written back as a stale total later.
    this.data[key] = (Number(this.data[key]) || 0) + amount;
    // The server applies the increment on top of the stored value, so a
    // plain value for the same field does not apply.
    delete this.changed[key];
    return this;
  }

  async save() {
    const payload = {};
    const fields = this.isNew
      ? Object.keys(this.data)
      : Object.keys(this.changed);
    fields.forEach((field) => {
      payload[field] = this.data[field];
    });
    if (this.isNew && !payload.createdAt) payload.createdAt = Date.now();
    if (this.collection === "chores") {
      payload.updatedAt = FieldValue.serverTimestamp();
    }
    // Take the pending increments before the write, so an increment that
    // lands while the write is in flight is not dropped.
    const pending = this.pending;
    this.pending = null;
    if (pending) {
      Object.keys(pending).forEach((field) => {
        payload[field] = FieldValue.increment(pending[field]);
      });
    }
    // Firestore rejects undefined values, and a missing field is not the
    // same as a null one.
    Object.keys(payload).forEach((field) => {
      if (payload[field] === undefined) delete payload[field];
    });
    if (Object.keys(payload).length > 0) {
      try {
        await firestore
          .collection(this.collection)
          .doc(this.id)
          .set(payload, { merge: true });
      } catch (error) {
        // Put the increments back, otherwise a failed write silently drops
        // them and the points counter loses the update.
        if (pending) {
          Object.keys(pending).forEach((field) => {
            this.pending = this.pending || {};
            this.pending[field] = (this.pending[field] || 0) + pending[field];
          });
        }
        throw error;
      }
    }
    this.isNew = false;
    // Clear only the fields this write covered, and only if they did not
    // change again while the write was in flight.
    fields.forEach((field) => {
      if (this.changed[field] && this.data[field] === payload[field]) {
        delete this.changed[field];
      }
    });
    if (pending) {
      await this.fetch();
    }
    return this;
  }

  async destroy() {
    await firestore.collection(this.collection).doc(this.id).delete();
  }

  /**
   * Re-reads the document and refreshes the local copy.
   *
   * A field that changed locally while the read was in flight keeps its local
   * value, and the dirty flags are left alone: a read is not a write.
   */
  async fetch() {
    const snapshot = await firestore.collection(this.collection).doc(this.id).get();
    if (snapshot.exists) {
      const server = snapshot.data();
      Object.keys(server).forEach((field) => {
        if (!this.changed[field]) this.data[field] = server[field];
      });
      this.isNew = false;
    }
    return this;
  }

  /** Kept for compatibility with the old Parse object id helper. */
  _getId() {
    return this.id;
  }
}

/**
 * @description The signed in family account, or null.
 *
 * A child device signs in anonymously so it can own its pairing record before
 * it joins a family. An anonymous session is not a family account, so it is
 * reported as signed out here.
 */
export function currentUser() {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) return null;
  return user;
}

/**
 * @description Signs in anonymously so a device can own its pairing record.
 *
 * The pairing record is only readable by the device that created it and by
 * the family that linked it, so the device needs an identity of its own. The
 * anonymous session cannot read any family data.
 * @return {Promise.<Object>} The Firebase user record.
 */
export async function signInAnonymously() {
  if (auth.currentUser) return auth.currentUser;
  const credential = await auth.signInAnonymously();
  return credential.user;
}

/**
 * @description Signs in as an anonymous user that belongs to this device.
 *
 * The device is about to join a family, so a session that is already signed
 * in is replaced. The pairing record is tied to the device that owns it, and
 * an old family account would tie it to that account instead.
 * @return {Promise.<Object>} The Firebase user record.
 */
async function anonymousDeviceSession() {
  if (auth.currentUser && !auth.currentUser.isAnonymous) {
    await auth.signOut();
    clearUserProfileCache();
  }
  return signInAnonymously();
}

/**
 * @description Reads a single record by id.
 * @return {Promise.<Record|null>}
 */
export async function getRecord(collection, id) {
  if (!id) return null;
  const snapshot = await firestore.collection(collection).doc(id).get();
  if (!snapshot.exists) return null;
  const record = new Record(collection, snapshot.id, snapshot.data());
  record.isNew = false;
  return record;
}

/**
 * @description Creates a record with a generated id and saves it.
 * @return {Promise.<Record>}
 */
export async function createRecord(collection, data = {}) {
  const record = new Record(
    collection,
    firestore.collection(collection).doc().id,
    data
  );
  await record.save();
  return record;
}

/**
 * @description Finds every record where a field matches a value.
 * @return {Promise.<Array<Record>>}
 */
export async function findRecords(collection, field, value, options = {}) {
  let query = firestore.collection(collection).where(field, "==", value);
  if (options.orderBy) {
    query = query.orderBy(options.orderBy, options.direction || "asc");
  }
  const snapshot = await query.get();
  return snapshot.docs.map((doc) => {
    const record = new Record(collection, doc.id, doc.data());
    record.isNew = false;
    return record;
  });
}

/**
 * @description Every child belonging to a parent account.
 * @return {Promise.<Array<Record>>}
 */
export async function getKids(parentId) {
  const kids = await findRecords("kids", "parentId", parentId);
  return kids.sort((a, b) => (a.get("createdAt") || 0) - (b.get("createdAt") || 0));
}

/**
 * @description Every chore for a child, newest first.
 * @return {Promise.<Array<Record>>}
 */
export async function getChores(childId) {
  return findRecords("chores", "childId", childId, {
    orderBy: "updatedAt",
    direction: "desc"
  });
}

/**
 * @description The seeded reward catalog.
 * @return {Promise.<Array<Record>>}
 */
export async function getRewards() {
  const snapshot = await firestore.collection("rewards").get();
  return snapshot.docs.map((doc) => {
    const record = new Record("rewards", doc.id, doc.data());
    record.isNew = false;
    return record;
  });
}

/**
 * @description Shared app config, such as the default tax rate.
 * @return {Promise.<Object>}
 */
export async function getAppConfig() {
  const record = await getRecord("config", "app");
  return record ? record.data : {};
}

let userProfile = null;

/**
 * @description The profile document for the signed in user, cached in memory.
 * @return {Promise.<Record|null>}
 */
export async function getUserProfile() {
  const user = currentUser();
  if (!user) return null;
  if (userProfile) return userProfile;
  const stored = await getRecord("users", user.uid);
  userProfile =
    stored ||
    new Record("users", user.uid, {
      email: user.email,
      username: user.email,
      app: "hero",
      logins: []
    });
  return userProfile;
}

/**
 * @description The profile without waiting on Firestore.
 *
 * Templates read the family email straight off the parent record, so this
 * cannot be an async call. It returns the cached profile when the document has
 * been loaded, and a profile built from the Firebase auth record otherwise.
 * @return {Record|null}
 */
export function getUserProfileSync() {
  const user = currentUser();
  if (!user) return null;
  if (userProfile) return userProfile;
  return new Record("users", user.uid, {
    email: user.email,
    username: user.email
  });
}

/**
 * @description Drops the cached profile, used on sign in and sign out.
 */
export function clearUserProfileCache() {
  userProfile = null;
}

/**
 * @description The parent account for the signed in user, created on first use.
 * @return {Promise.<Record>}
 */
export async function getParent() {
  const user = currentUser();
  if (!user) throw new Error("No signed in user");
  const existing = await getRecord("parents", user.uid);
  if (existing) return existing;
  return createParent();
}

/**
 * @description Creates the parent account for the signed in user.
 * @return {Promise.<Record>}
 */
export async function createParent() {
  const user = currentUser();
  if (!user) throw new Error("No signed in user");
  const parent = new Record("parents", user.uid, {
    userId: user.uid,
    role: "ParentAccount",
    slimView: true,
    createdAt: Date.now()
  });
  await parent.save();
  try {
    await CCStripe.newCustomer(parent);
  } catch (e) {
    // A missing Stripe customer should not block the family account. The
    // customer is created again the next time the parent opens billing.
    console.error("Could not create the Stripe customer", e);
  }
  return parent;
}

/**
 * @description Signs the user out and resets the app state.
 */
export async function logout() {
  await auth.signOut();
  clearUserProfileCache();
  // Vue 2 only allows $data to be replaced in development builds, so copy
  // the fields instead of replacing the object.
  Object.assign(VueInstance.$root.$data, VueInstance.originalValue());
  setTimeout(() => location.reload(true), 500);
  location.href = "/#/";
}

/**
 * @description Picks the illustration that matches a chore title.
 */
function choreImage(title) {
  const titleIncludes = (word) => title.toLowerCase().includes(word);
  if (titleIncludes("brush teeth")) return "/images/chores/brushTeeth.jpg";
  if (titleIncludes("clean") || titleIncludes("kitchen"))
    return "/images/chores/kitchen.jpeg";
  if (titleIncludes("empty dish"))
    return "/images/chores/empty_dishwasher.jpeg";
  if (
    titleIncludes("dishes") ||
    titleIncludes("plato") ||
    titleIncludes("traste") ||
    titleIncludes("vajilla")
  )
    return "/images/chores/dishes.jpeg";
  if (titleIncludes("rake") || titleIncludes("rastrillar"))
    return "/images/chores/rake.jpeg";
  if (titleIncludes("weed")) return "/images/chores/weed.jpeg";
  if (titleIncludes("lawn") || titleIncludes("grass") || titleIncludes("mow") || titleIncludes("césped"))
    return "/images/chores/mow.jpg";
  if (titleIncludes("sweep")) return "/images/chores/sweep.jpg";
  if (titleIncludes("dust")) return "/images/chores/dust.jpg";
  if (titleIncludes("trash") || titleIncludes("rubbish") || titleIncludes("garbage"))
    return "/images/chores/trash.jpg";
  if (titleIncludes("cook") || titleIncludes("bake") || titleIncludes("dinner"))
    return "/images/chores/bake.jpg";
  if (titleIncludes("shower") || titleIncludes("bathe") || titleIncludes("take a bath"))
    return "/images/chores/shower.jpeg";
  if (titleIncludes("medicine") || titleIncludes("take meds"))
    return "/images/chores/meds.jpeg";
  if (
    titleIncludes("scripture") ||
    titleIncludes("praise") ||
    titleIncludes("bible study") ||
    titleIncludes("worship")
  )
    return "/images/chores/praise.jpeg";
  if ((titleIncludes("read") && !titleIncludes("ready")) || titleIncludes("book"))
    return "/images/chores/reading.jpg";
  if (titleIncludes("vacuum")) return "/images/chores/vacuum.jpg";
  if (titleIncludes("laundry")) return "/images/chores/laundry.jpg";
  if (titleIncludes("cat")) return "/images/chores/cat.jpg";
  if (titleIncludes("litter")) return "/images/chores/litter.jpg";
  if (titleIncludes("bathroom")) return "/images/chores/bathroom.jpg";
  if (
    titleIncludes("bedroom") ||
    titleIncludes("make bed") ||
    titleIncludes("tidy room")
  )
    return "/images/chores/tidy-bedroom.jpg";
  return undefined;
}

/**
 * @description Adds a chore to a child.
 */
export async function addChore(kid, choreData) {
  const chore = new Record(
    "chores",
    firestore.collection("chores").doc().id,
    {
      name: choreData.title,
      points: Number(choreData.points),
      done: choreData.done,
      childId: kid.id,
      repeats: choreData.repeats,
      photoVerify: false,
      image: choreImage(choreData.title) || null,
      createdAt: Date.now()
    }
  );
  await chore.save();
  gtag("event", "add_chore", {
    name: choreData.title,
    points: choreData.points,
    repeats: choreData.repeats
  });
}

/**
 * @description Updates the fields of an existing chore.
 */
export async function editChore(kid, chore, choreData) {
  chore.set("name", choreData.title);
  chore.set("points", Number(choreData.points));
  chore.set("done", choreData.done);
  chore.set("childId", kid.id);
  chore.set("repeats", choreData.repeats);
  chore.set("photoVerify", false);
  chore.set("image", choreImage(choreData.title) || null);
  await chore.save();
}

/**
 * @description Adds a child to the signed in family account.
 */
export async function addChild(_t) {
  const parent = await getParent();
  const kid = new Record(
    "kids",
    firestore.collection("kids").doc().id,
    {
      name: _t.$data.name.replace(/[^\w ]/g, "").trim(),
      age: Math.floor(_t.$data.age),
      points: 0,
      parentId: parent.id,
      image: childImage(_t.$data.image),
      createdAt: Date.now()
    }
  );
  await kid.save();
  gtag("event", "add_child");
  return true;
}

/**
 * @description Keeps an avatar under the Firestore document size limit.
 */
function childImage(image) {
  if (typeof image !== "string") return null;
  if (!image.startsWith("data:image")) return null;
  return image.length > 400000 ? null : image;
}

/**
 * @description Adds a child from a pairing code shown on the child device.
 */
export async function addChildViaLinkCode(linkCode) {
  const device = await getRecord("linkableDevices", linkCode);
  if (!device) throw new Error("Pairing code not found");
  // A code that has already been claimed cannot be used again. Otherwise
  // the same code would add the same child a second time.
  if (device.get("hasBeenLinked")) {
    throw new Error("That device is already linked to a family account");
  }
  const parent = await getParent();
  const kid = new Record(
    "kids",
    firestore.collection("kids").doc().id,
    {
      name: device.get("childName"),
      age: Number(device.get("childAge")),
      points: 0,
      parentId: parent.id,
      createdAt: Date.now()
    }
  );
  const confirmAdding = await ModernModals.prompt(
    `Would you like to add ${device.get("childName")} to your account?`
  );
  if (!confirmAdding) {
    ModernModals.alert("Adding child has been cancelled.");
    return false;
  }
  // Link the device first. If the family password is not confirmed, nothing
  // is created, so the parent does not end up with a child that is not
  // actually paired.
  const linked = await confirmLinking(linkCode);
  if (!linked) return false;
  await kid.save();
  VueInstance.$root.initLoadChores();
  window.location = "/#/parents";
  return true;
}

/**
 * @description Links a child device that is waiting with a pairing code.
 *
 * The record carries no credentials. The child device holds the family
 * account details and signs itself in once the link is made, so the family
 * password never reaches the server.
 */
export async function confirmLinking(id) {
  const device = await getRecord("linkableDevices", id);
  if (!device) throw new Error("Pairing code not found");
  const user = currentUser();
  if (!user) throw new Error("No signed in user");
  device.set("linkedByUid", user.uid);
  device.set("hasBeenLinked", true);
  await device.save();
  return true;
}

/**
 * @description Creates the pairing record a child device shows as a QR code.
 *
 * The device signs in anonymously first so the record can be tied to it. That
 * anonymous session cannot read any family data, it only owns this one record.
 */
export async function createLinkableDevice(name, age) {
  const childName = String(name || "").trim();
  const childAge = Number(age);
  // The rules ask for a name, so an empty one is rejected before the record
  // is written and the failure comes back as a clear message.
  if (!childName) throw new Error("Enter the name of the child for this device");
  if (!Number.isFinite(childAge) || childAge < 3 || childAge > 17) {
    throw new Error("Enter an age between 3 and 17");
  }
  const deviceUser = await anonymousDeviceSession();
  const device = new Record(
    "linkableDevices",
    firestore.collection("linkableDevices").doc().id,
    {
      childName: childName,
      childAge: childAge,
      hasBeenLinked: false,
      deviceUid: deviceUser.uid,
      // Always written, null until the family claims the record. The rules
      // read it directly, and a missing field is an error there.
      linkedByUid: null,
      createdAt: Date.now()
    }
  );
  await device.save();
  window.localStorage.setItem("deviceKey", device.id);
  const QRCode = require("qrcode-svg");
  const svg = new QRCode({
    padding: 0,
    height: 200,
    width: 200,
    content: `${device.id}`,
    container: "svg-viewbox",
    background: "transparent",
    color:
      window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "white"
        : "black"
  }).svg();
  return { svg: svg, id: device.id };
}

/**
 * @description Waits for a child device to be paired.
 *
 * The record only says whether the family linked it. The family account
 * details stay on the device that typed them.
 */
export async function loginViaLink() {
  const deviceKey = window.localStorage.getItem("deviceKey");
  const device = await getRecord("linkableDevices", deviceKey);
  if (!device) throw new Error("This device is no longer waiting to be paired");
  // The device is still waiting, so the caller keeps polling.
  if (!device.get("hasBeenLinked")) return false;
  return { linked: true };
}

/**
 * @description Removes a pairing record once the device has joined.
 *
 * The code is the secret, so it is deleted as soon as it has been used. A
 * code that is still around would let another family claim the device.
 */
export async function removeLinkableDevice(id) {
  if (!id) return;
  const device = await getRecord("linkableDevices", id);
  if (!device) return;
  await device.destroy();
}

/**
 * @description Stores the Patreon authorization for the family account.
 */
export async function setUserPatreonToken(code, token) {
  const parent = await getParent();
  parent.set("patreonAuthCode", code);
  parent.set("patreonAuthToken", token);
  await parent.save();
}
